import { useEffect, useRef } from 'react';

/* The hero's figure: discourse arriving as a wide scatter on the left,
   narrowing to a single stream, then organising into a braided pair of
   strands on the right — structure emerging from noise. Thousands of
   particles drift left → right and are rendered on a coarse pixel grid, so
   the whole thing reads as a mosaic; a pulse travels down the stream so it
   never sits still. Monochrome ink on paper. Respects reduced motion. */
export function HeroWave({ className }: { className?: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return;
    const ctx = canvas.getContext('2d'); if (!ctx) return;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const CELL = 4, N = 4200, PULSE = 4600;
    let W = 0, H = 0, dpr = 1, raf = 0, cols = 0, rows = 0, dens = new Float32Array(0);
    let seed = 11; const rnd = () => { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; };
    const gauss = () => Math.sqrt(-2 * Math.log(1 - rnd())) * Math.cos(2 * Math.PI * rnd());
    type P = { u: number; n: number; s: number; v: number; w: number; j: number }; // s: strand phase, three strands
    let ps: P[] = [];
    const build = () => {
      seed = 11; ps = [];
      for (let i = 0; i < N; i++) ps.push({ u: rnd(), n: gauss(), s: (i % 3) * 2.094, v: 0.028 + rnd() * 0.022, w: 0.4 + rnd() * 0.6, j: gauss() });
    };
    const resize = () => {
      dpr = Math.min(2, window.devicePixelRatio || 1);
      W = canvas.clientWidth; H = canvas.clientHeight;
      canvas.width = Math.floor(W * dpr); canvas.height = Math.floor(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(W / CELL); rows = Math.ceil(H / CELL); dens = new Float32Array(cols * rows);
      build();
    };
    const ss = (k: number) => k <= 0 ? 0 : k >= 1 ? 1 : k * k * (3 - 2 * k);
    // Lateral spread: wide on the left, a thread by the middle.
    const spread = (u: number) => H * (0.46 * Math.pow(1 - ss(u / 0.5), 1.3) + 0.01);
    // Braid amplitude: nothing until the middle, then the strands open out to the full height at the right edge.
    const braid = (u: number) => H * 0.44 * ss((u - 0.5) / 0.42);
    let t0 = performance.now();
    const draw = (now: number) => {
      const dt = reduce ? 0 : Math.min(0.05, (now - t0) / 1000); t0 = now;
      const phase = ((now % PULSE) / PULSE) * 1.25 - 0.12;       // the pulse, left → right
      dens.fill(0);
      const cy = H / 2;
      for (const p of ps) {
        p.u += p.v * dt; if (p.u > 1) p.u -= 1;
        const u = p.u, x = u * W;
        const y = cy + p.n * spread(u) + braid(u) * Math.sin(u * 17 + p.s + now * 0.0005) + p.j * H * 0.03 * ss((u - 0.5) / 0.5);
        const cx = (x / CELL) | 0, cyy = (y / CELL) | 0;
        if (cx < 0 || cx >= cols || cyy < 0 || cyy >= rows) continue;
        const pulse = Math.exp(-Math.pow((u - phase) / 0.075, 2));
        dens[cyy * cols + cx] = dens[cyy * cols + cx]! + p.w * (0.07 + 0.11 * u + 0.5 * pulse);
      }
      ctx.clearRect(0, 0, W, H);
      for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) {
        const d = dens[r * cols + c]!; if (d < 0.04) continue;
        const a = Math.min(0.9, d * 0.95);
        ctx.fillStyle = `rgba(24,24,27,${a.toFixed(3)})`;
        ctx.fillRect(c * CELL, r * CELL, CELL - 0.8, CELL - 0.8);
      }
      if (!reduce) raf = requestAnimationFrame(draw);
    };
    resize();
    const ro = new ResizeObserver(resize); ro.observe(canvas);
    raf = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, []);
  return <canvas ref={ref} className={className} aria-hidden="true" />;
}
